import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BookmarkPlus, Layers, Trash2, Loader2, Sparkles } from "lucide-react";

export default function PhaseTemplatesDialog({ wsId, projectId, phaseCount, onApplied }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [saveName, setSaveName] = useState("");
  const [saveIncludeTasks, setSaveIncludeTasks] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!wsId || !open) return;
    setBusy(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/phase-templates`);
      setTemplates(r.data.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load templates");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, open]);

  const runApply = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const r = await api.post(`/projects/${projectId}/apply-phase-template/${confirm.id}`);
      const d = r.data || {};
      toast.success(`Applied · ${d.created_phases} phase${d.created_phases === 1 ? "" : "s"}, ${d.created_tasks} task${d.created_tasks === 1 ? "" : "s"}`);
      setConfirm(null);
      setOpen(false);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setBusy(false); }
  };

  const runSave = async () => {
    if (!saveName.trim()) { toast.info("Template name required"); return; }
    setSaving(true);
    try {
      await api.post(`/workspaces/${wsId}/projects/${projectId}/save-as-phase-template`, {
        name: saveName.trim(),
        include_tasks: saveIncludeTasks,
      });
      toast.success(`Saved template "${saveName.trim()}"`);
      setSaveName(""); setShowSave(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const runDelete = async (tid) => {
    try {
      await api.delete(`/workspaces/${wsId}/phase-templates/${tid}`);
      toast.success("Template deleted");
      setTemplates((p) => p.filter((t) => t.id !== tid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 h-7 px-2 border border-dashed border-border hover:bg-muted text-[11px] rounded-none text-muted-foreground"
        data-testid="phase-templates-open">
        <BookmarkPlus className="w-3 h-3" /> Templates
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-4 h-4" /> Phase templates
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {busy && templates.length === 0 && (
              <div className="text-xs text-muted-foreground p-3 text-center">Loading…</div>
            )}
            {!busy && templates.length === 0 && (
              <div className="text-xs text-muted-foreground p-3 text-center">
                No templates yet. Save this project's phases as a reusable template.
              </div>
            )}
            {templates.map((t) => (
              <div key={t.id} className="border border-border p-2 flex items-center gap-2"
                   data-testid={`phase-template-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{t.name}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {t.phase_count || 0} phases · {t.task_count || 0} tasks
                    {t.created_by_name && <> · by {t.created_by_name}</>}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="h-7 rounded-none"
                        onClick={() => setConfirm(t)}
                        data-testid={`phase-template-apply-${t.id}`}>
                  <Sparkles className="w-3 h-3 mr-1" /> Apply
                </Button>
                <Button size="sm" variant="ghost" className="h-7 rounded-none text-rose-600"
                        onClick={() => runDelete(t.id)}
                        data-testid={`phase-template-delete-${t.id}`}
                        title="Delete template">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter className="flex sm:justify-between gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setShowSave(true)}
                    disabled={phaseCount === 0}
                    data-testid="phase-templates-save">
              <BookmarkPlus className="w-3 h-3 mr-1" />
              Save current as template
            </Button>
            <Button variant="ghost" className="rounded-none" onClick={() => setOpen(false)}
                    data-testid="phase-templates-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save-as dialog */}
      <Dialog open={showSave} onOpenChange={setShowSave}>
        <DialogContent className="max-w-sm rounded-none">
          <DialogHeader><DialogTitle>Save phases as template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Name</div>
              <Input value={saveName} onChange={(e) => setSaveName(e.target.value)}
                     placeholder="e.g. Agency Standard"
                     className="rounded-none h-8 text-sm"
                     data-testid="phase-templates-save-name" />
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={saveIncludeTasks}
                     onChange={(e) => setSaveIncludeTasks(e.target.checked)}
                     data-testid="phase-templates-save-include-tasks" />
              Include tasks (title, priority, budget) as templates
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setShowSave(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={runSave} disabled={saving}
                    data-testid="phase-templates-save-run">
              {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply confirm */}
      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent className="max-w-sm rounded-none">
          <DialogHeader><DialogTitle>Apply "{confirm?.name}"?</DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground">
            This will add {confirm?.phase_count || 0} phase{confirm?.phase_count === 1 ? "" : "s"} and
            {" "}{confirm?.task_count || 0} task{confirm?.task_count === 1 ? "" : "s"} to this project.
            Existing phases stay untouched.
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button className="rounded-none" disabled={busy} onClick={runApply}
                    data-testid="phase-templates-apply-run">
              {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
